/**
 * useDragDrop Hook for UI-Model-1
 *
 * Provides drag-drop utilities including:
 * - Insert position calculation from cursor Y
 * - Drag item tracking
 * - Integration with command dispatch
 */

import { useCallback } from 'react';
import { useDragDropContextOptional } from '../contexts/DragDropContext';

// ============================================================================
// Types
// ============================================================================

export interface DragItem {
  /** Unique ID of the dragged item */
  id: string;
  /** Type of the item being dragged */
  type: 'field' | 'category' | 'connector' | 'widget' | 'submodel';
  /** Source of the drag (library or container) */
  source: 'library' | 'container';
  /** Container ID if dragged from a container */
  sourceContainerId?: string;
  /** Original index in source container */
  sourceIndex?: number;
  /** Additional drag data */
  data?: Record<string, unknown>;
}

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Calculate the insert index based on cursor Y position relative to items
 * @param containerEl - The container DOM element
 * @param dropY - The Y coordinate of the drop position (clientY)
 * @returns The index where the item should be inserted
 */
export const calculateInsertIndex = (
  containerEl: HTMLElement,
  dropY: number
): number => {
  // Find all item elements within the container
  const itemElements = containerEl.querySelectorAll<HTMLElement>('[data-item-id]');

  if (itemElements.length === 0) {
    return 0; // Empty container, insert at start
  }

  // Iterate through items to find the insertion point
  for (let i = 0; i < itemElements.length; i++) {
    const rect = itemElements[i].getBoundingClientRect();
    const midpoint = rect.top + rect.height / 2;

    if (dropY < midpoint) {
      return i; // Insert before this item
    }
  }

  // Drop is below all items, append at end
  return itemElements.length;
};

/**
 * Calculate insert index from a DragEvent
 * @param e - The drag event
 * @param containerId - The container ID to find items within
 * @returns The calculated insert index
 */
export const calculateInsertIndexFromEvent = (
  e: React.DragEvent,
  containerId: string
): number => {
  const containerEl = document.getElementById(`container-${containerId}`);
  if (!containerEl) {
    return 0;
  }
  return calculateInsertIndex(containerEl, e.clientY);
};

/**
 * Get the item element at a specific position
 * @param containerEl - The container DOM element
 * @param y - The Y coordinate to check
 * @returns The item element at that position, or null
 */
export const getItemAtPosition = (
  containerEl: HTMLElement,
  y: number
): HTMLElement | null => {
  const itemElements = containerEl.querySelectorAll<HTMLElement>('[data-item-id]');

  for (let i = 0; i < itemElements.length; i++) {
    const item = itemElements[i];
    const rect = item.getBoundingClientRect();
    if (y >= rect.top && y <= rect.bottom) {
      return item;
    }
  }

  return null;
};

// ============================================================================
// Hook
// ============================================================================

export const useDragDrop = () => {
  const dragDropContext = useDragDropContextOptional();

  /**
   * Check if a drag operation is currently in progress
   */
  const isDragging = dragDropContext?.dragState.isDragging ?? false;

  /**
   * Get the current drag state
   */
  const dragState = dragDropContext?.dragState ?? null;

  /**
   * Get the current drop indicator state
   */
  const dropIndicator = dragDropContext?.dropIndicator ?? null;

  /**
   * Update the drop indicator position
   */
  const updateDropIndicator = useCallback(
    (containerId: string | null, index: number, isVisible: boolean) => {
      dragDropContext?.setDropIndicator({
        containerId,
        index,
        isVisible,
        position: null, // Position can be calculated by components if needed
      });
    },
    [dragDropContext]
  );

  /**
   * Parse drag data from a DragEvent
   */
  const parseDragData = useCallback((e: React.DragEvent): DragItem | null => {
    try {
      const data = e.dataTransfer.getData('application/json');
      if (!data) return null;

      const parsed = JSON.parse(data);

      return {
        id: parsed.id,
        type: parsed.type,
        source: parsed.source || 'library',
        sourceContainerId: parsed.containerId,
        sourceIndex: parsed.index,
        data: parsed,
      };
    } catch {
      return null;
    }
  }, []);

  /**
   * Check if drop is allowed in a container
   */
  const canDropInContainer = useCallback(
    (containerId: string, dragItem: DragItem | null): boolean => {
      if (!dragItem) return false;

      // Allow drops from library
      if (dragItem.source === 'library') return true;

      // Allow drops from other containers (cross-container move)
      if (dragItem.source === 'container') return true;

      return false;
    },
    []
  );

  return {
    // State
    isDragging,
    dragState,
    dropIndicator,
    // Actions
    updateDropIndicator,
    // Utilities
    calculateInsertIndex,
    calculateInsertIndexFromEvent,
    getItemAtPosition,
    parseDragData,
    canDropInContainer,
  };
};

export default useDragDrop;
